import type { Dispatch, ReactNode, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TextInput, type RaRecord } from "react-admin";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { useFormContext, useWatch } from "react-hook-form";
import {
  NO_EXTRA_ITS_FILTER,
  itsAutoDefaultHelperText,
  useItsAutoLookup,
} from "@/components/its-lookup";
import { resolveRazaEventVenueAddress } from "@/utils/raza-event-venue-address";
import {
  isNonEmptyTrimmedString,
  shouldClearAddressOnHostItsChange,
  shouldPrefillAddressFromIts,
  shouldPrefillHostFieldFromIts,
} from "./raza-event-its-prefill";

const defaultHelperText = itsAutoDefaultHelperText();

type LookupHint =
  | { variant: "default" }
  | { variant: "notInDirectory"; its: string }
  | { variant: "lookupError"; message: string };

function setDefaultHintIfNeeded(setter: Dispatch<SetStateAction<LookupHint>>) {
  setter((prev) => (prev.variant === "default" ? prev : { variant: "default" }));
}

function hintToHelperNode(hint: LookupHint): ReactNode {
  if (hint.variant === "default") {
    return defaultHelperText;
  }
  if (hint.variant === "notInDirectory") {
    return (
      <Box component="span" sx={{ typography: "caption", color: "text.secondary" }}>
        ITS {hint.its} is not in the directory — enter the host name manually in the field below
      </Box>
    );
  }
  return (
    <Box component="span" sx={{ typography: "caption", color: "error.main" }}>
      {hint.message}
    </Box>
  );
}

type RazaEventITSLookupProps = {
  source: string;
  label: string;
  optional?: boolean;
  filter?: Record<string, unknown>;
};

/**
 * Host ITS: no search button — when a complete ITS is entered, itsdata is queried (debounced).
 */
export default function RazaEventITSLookup({
  source,
  label,
  optional = false,
  filter = NO_EXTRA_ITS_FILTER,
}: RazaEventITSLookupProps) {
  const { setValue, getValues } = useFormContext();
  const hallBookings = useWatch({ name: "hallBookings" });
  const [lookupHint, setLookupHint] = useState<LookupHint>({ variant: "default" });
  const prevHostItsRef = useRef<string | undefined>(undefined);
  const hostItsChangedRef = useRef(false);

  const hasHallVenue = useMemo(
    () => resolveRazaEventVenueAddress({ hallBookings }).source === "hall",
    [hallBookings]
  );

  const clearHostDetailsOnItsChange = useCallback(() => {
    const hasStoredAddress = isNonEmptyTrimmedString(getValues("address"));
    setValue("hostName", "");
    setValue("mobileNo", "");
    if (shouldClearAddressOnHostItsChange({ hasHallVenue, hasStoredAddress })) {
      setValue("address", "");
    }
  }, [getValues, hasHallVenue, setValue]);

  const onFound = useCallback(
    (row: RaRecord, _its: string) => {
      setValue(source, row.ITS_ID);
      if (source === "hostItsNo") {
        const hostItsChanged = hostItsChangedRef.current;
        const hasStoredAddress = isNonEmptyTrimmedString(getValues("address"));
        const prefillAddress = shouldPrefillAddressFromIts({
          hasHallVenue,
          hasStoredAddress,
        });

        if (
          shouldPrefillHostFieldFromIts({
            hostItsChanged,
            currentValue: getValues("hostName"),
          })
        ) {
          setValue("hostName", row.Full_Name ?? "");
        }
        if (
          shouldPrefillHostFieldFromIts({
            hostItsChanged,
            currentValue: getValues("mobileNo"),
          })
        ) {
          setValue("mobileNo", row.Mobile);
        }
        if (prefillAddress) {
          setValue("address", row.Address);
        }
      }
      setDefaultHintIfNeeded(setLookupHint);
    },
    [getValues, hasHallVenue, setValue, source]
  );

  const onNotFound = useCallback((its: string) => {
    setLookupHint({ variant: "notInDirectory", its });
  }, []);

  const onError = useCallback((e: unknown) => {
    console.error(e);
    setLookupHint({
      variant: "lookupError",
      message: e instanceof Error ? e.message : "ITS lookup failed",
    });
  }, []);

  const onIncomplete = useCallback(() => {
    if (source === "hostItsNo") {
      clearHostDetailsOnItsChange();
    }
    setDefaultHintIfNeeded(setLookupHint);
  }, [clearHostDetailsOnItsChange, source]);

  const { loading, currentValue } = useItsAutoLookup({
    source,
    filter,
    onFound,
    onNotFound,
    onError,
    onIncomplete,
  });

  useEffect(() => {
    if (source !== "hostItsNo") return;
    const prev = prevHostItsRef.current;
    if (prev !== undefined && prev !== currentValue) {
      hostItsChangedRef.current = true;
      clearHostDetailsOnItsChange();
      setDefaultHintIfNeeded(setLookupHint);
    }
    prevHostItsRef.current = currentValue;
  }, [clearHostDetailsOnItsChange, currentValue, source]);

  const helperText = useMemo(() => hintToHelperNode(lookupHint), [lookupHint]);

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, width: "100%" }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextInput
          source={source}
          label={label}
          fullWidth
          helperText={helperText}
          isRequired={!optional}
        />
      </Box>
      {loading ? <CircularProgress size={22} sx={{ mt: 2 }} /> : null}
    </Box>
  );
}
